/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Account-wide resource overview shared by Home and the All services hub.
// Counts span every project the user can see (same as the previous Dashboard),
// so the numbers don't change when the top-bar project picker changes.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  walletService,
  projectService,
  containerService,
  volumeService,
  bucketService,
  sandboxService,
  managedServiceApi,
  pricingService,
} from '../../services/api';
import { isManagedMLOpsEnabled, isSandboxEnabled } from '../../config/featureFlags';
import type { GPUAvailability, GPUPricing, UserExpenditureSummary, WalletSummary } from '../../types';
import type { ManagedServiceInstance } from '../../types/managedServices';

const asList = (data: any, ...keys: string[]): any[] => {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
};

/** Run `fetch` for every project and concatenate the lists; per-project failures are ignored. */
const perProject = async (
  projectIds: string[],
  fetch: (id: string) => Promise<{ data: any }>,
  ...keys: string[]
) => {
  const results = await Promise.allSettled(projectIds.map((id) => fetch(id)));
  return results.flatMap((r) => (r.status === 'fulfilled' ? asList(r.value.data, 'data', ...keys) : []));
};

export const volumeSizeGb = (v: any): number => {
  const gb = Number(v?.size_gb ?? v?.quota_gb ?? 0);
  if (gb > 0) return gb;
  const bytes = Number(v?.max_quota ?? 0);
  return bytes > 0 ? bytes / 1024 ** 3 : 0;
};

export const useAccountOverview = () => {
  const { data: walletData, isLoading: walletLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletService.get().then((res) => res.data),
    refetchInterval: 30000, // Poll every 30 seconds for balance updates
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.list().then((res) => res.data),
  });
  const projectIds: string[] = useMemo(
    () => asList(projectsData, 'projects').map((p: any) => p.id).filter(Boolean),
    [projectsData],
  );
  const haveProjects = !projectsLoading;

  const { data: containers = [], isLoading: containersLoading } = useQuery({
    queryKey: ['all-containers', projectIds],
    queryFn: () => perProject(projectIds, (id) => containerService.list(id), 'containers'),
    enabled: haveProjects,
    refetchInterval: 10000, // Poll every 10 seconds for live updates
  });

  const { data: volumes = [], isLoading: volumesLoading } = useQuery({
    queryKey: ['all-volumes', projectIds],
    queryFn: () => perProject(projectIds, (id) => volumeService.list(id), 'storages', 'volumes'),
    enabled: haveProjects,
  });

  const { data: buckets = [], isLoading: bucketsLoading } = useQuery({
    queryKey: ['all-buckets', projectIds],
    queryFn: () => perProject(projectIds, (id) => bucketService.list(id), 'buckets'),
    enabled: haveProjects,
  });

  const sandboxesOn = isSandboxEnabled();
  const { data: sandboxes = [] } = useQuery({
    queryKey: ['all-sandboxes'],
    queryFn: () => sandboxService.list(undefined, 1, 100).then((res) => asList(res.data, 'items', 'data')),
    enabled: sandboxesOn,
    retry: 1,
  });

  const mlopsOn = isManagedMLOpsEnabled();
  const { data: managed = [] } = useQuery({
    queryKey: ['managed-services'],
    queryFn: () => managedServiceApi.list().then((res) => res.data as ManagedServiceInstance[]),
    enabled: mlopsOn,
  });

  const { data: expenditure, isLoading: expenditureLoading } = useQuery({
    queryKey: ['expenditure'],
    queryFn: () => walletService.getExpenditure().then((res) => res.data as UserExpenditureSummary).catch(() => null),
  });

  const { data: gpuPricingData } = useQuery({
    queryKey: ['gpu-pricing'],
    queryFn: () => pricingService.getGPUPricing().then((res) => res.data),
  });
  const { data: gpuAvailabilityData } = useQuery({
    queryKey: ['gpu-availability'],
    queryFn: () => pricingService.getGPUAvailability().then((res) => res.data),
  });

  const wallet: WalletSummary | null = walletData?.wallet || walletData || null;

  const gpus = useMemo(() => {
    const pricing = (asList(gpuPricingData, 'data') as GPUPricing[]).filter((g) => g.is_active !== false);
    const availability = asList(gpuAvailabilityData, 'data') as GPUAvailability[];
    const freeTypes = pricing
      .filter((g) => (availability.find((a) => a.gpu_type === g.gpu_type)?.available_gpus ?? 0) > 0)
      .map((g) => g.gpu_type);
    const cheapest = pricing
      .filter((g) => Number(g.price_per_minute) > 0)
      .sort((a, b) => a.price_per_minute - b.price_per_minute)[0];
    return {
      types: pricing.map((g) => g.gpu_type),
      freeTypes,
      hasAvailabilityData: availability.length > 0,
      cheapest: cheapest ? { type: cheapest.gpu_type, perHour: cheapest.price_per_minute * 60 } : null,
    };
  }, [gpuPricingData, gpuAvailabilityData]);

  const runningPods = containers.filter((c: any) => (c.status || '').toLowerCase() === 'running').length;
  const runningSandboxes = sandboxes.filter((s: any) => s.state === 'Running').length;
  const activeManaged = managed.filter((m) => m.status === 'active' || m.status === 'provisioning').length;
  const volumeGb = volumes.reduce((sum: number, v: any) => sum + volumeSizeGb(v), 0);

  return {
    wallet,
    walletLoading,
    currency: wallet?.currency || 'INR',
    containers,
    runningPods,
    volumes,
    volumeGb,
    buckets,
    sandboxes,
    runningSandboxes,
    managed,
    activeManaged,
    expenditure: expenditure || null,
    gpus,
    loading: projectsLoading || containersLoading || volumesLoading || bucketsLoading,
    expenditureLoading,
  };
};

/** Compact money: ₹10.5K above 1,000, otherwise two decimals. */
export const formatCompact = (value: number, currency = 'INR') => {
  const sym = currency === 'USD' ? '$' : '₹';
  const v = Number(value || 0);
  if (Math.abs(v) >= 1000) return `${sym}${(v / 1000).toFixed(1)}K`;
  return `${sym}${v.toFixed(2)}`;
};

export const formatGb = (gb: number) =>
  gb >= 1024 ? `${(gb / 1024).toFixed(1)} TB` : `${Math.round(gb).toLocaleString('en-IN')} GB`;
